import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { runMultiplexTissueSegmentationMask } from 'api/platform';
import { modelTypeTissueSegmentationMask } from 'components/Pages/Jobs/inferenceFieldsOptions';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import { useCasesParams } from '../../../../../utils/useCasesParams';
import { ActionStepper } from '../ActionStepper';
import { ExecutionRequirementsPanel } from '../requirements/ExecutionRequirementsPanel';
import { ExecutionRequirementType, useExecutionRequirements } from '../requirements/useExecutionRequirements';
import { SelectModelStep } from '../SelectModelStep';
import { JobNameAndDescriptionStep } from '../utils';
import { MultiplexSelectionAlert } from './MultiplexSelectionAlert';
import { useMultiplexActionSelection } from './useMultiplexActionSelection';

const SNACK_BAR_KEY_RUN_MULTIPLEX_TISSUE_SEGMENTATION_MASK = 'RUN_MULTIPLEX_TISSUE_SEGMENTATION_MASK';

export interface IFormValues {
  modelPath: string;
  jobName: string;
  jobDescription?: string;
}

const modelUrlValidation = yup.object({
  modelPath: yup.string().required('Model Path is required'),
});

const validationSchema = [
  modelUrlValidation,
  yup.object({
    jobName: yup.string().optional().nullable(),
    jobDescription: yup.string().optional().nullable(),
  }),
];

const defaultValues = { modelPath: '' };

export interface IFormProps {
  onClose: (event?: any, force?: boolean) => void;
}

export const RunMultiplexTissueSegmentationMask = (props: IFormProps): ReactElement => {
  const { onClose } = props;
  const { casesParams } = useCasesParams();
  const {
    effectiveCasesParams,
    infoMessage: multiplexSelectionInfoMessage,
    blockingMessage: multiplexSelectionBlockingMessage,
    isLoadingSelection,
  } = useMultiplexActionSelection(casesParams);
  const multiplexCasesParams = effectiveCasesParams || casesParams;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const activeStateHandlers = useState(0);
  const [activeStep, setActiveStep] = activeStateHandlers;
  const currentValidationSchema = validationSchema[activeStep];
  const formMethods = useForm<IFormValues>({
    mode: 'onChange',
    resolver: yupResolver(currentValidationSchema),
    defaultValues,
  });
  useTrackActionFormDirty(formMethods.formState.isDirty);
  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors },
  } = formMethods;
  const executionRequirements = useExecutionRequirements([
    {
      type: ExecutionRequirementType.CloudOptimizedSlides,
      label: 'Cloud-optimized slides',
      casesParams: multiplexCasesParams,
      slideIds: multiplexCasesParams.slideIdsToInclude,
      enabled: Boolean(multiplexCasesParams.studyId || multiplexCasesParams.filters?.studyId),
    },
  ]);

  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});

  const checkValidationAndSetIsStepFailed = async (stepIndex: number) => {
    const isValid = await trigger();

    setIsStepFailed((prev) => ({
      ...prev,
      [stepIndex]: !isValid,
    }));

    return isValid;
  };
  const runMultiplexTissueSegmentationMaskMutation = useMutation(runMultiplexTissueSegmentationMask, {
    onError: () => {
      enqueueSnackbar('Error occurred, Multiplex Tissue Segmentation Mask failed', {
        variant: 'error',
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Multiplex Tissue Segmentation Mask Started', { variant: 'success' });
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_MULTIPLEX_TISSUE_SEGMENTATION_MASK);
    },
  });

  const onSubmit = async () => {
    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for Multiplex Tissue Segmentation Mask to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_MULTIPLEX_TISSUE_SEGMENTATION_MASK,
      autoHideDuration: null,
    });

    await runMultiplexTissueSegmentationMaskMutation.mutateAsync({
      ...multiplexCasesParams,
      ...formMethods.getValues(),
    });

    onClose(undefined, true);
  };

  const steps = [
    {
      label: 'Select Tissue Segmentation Model',
      subLabel: watch('modelPath') || 'Please select a model',
      content: (
        <Grid
          container
          direction="column"
          spacing={2}
          mt={1}
          maxHeight="60vh"
          sx={{ overflowY: 'auto' }}
          flexWrap="nowrap"
        >
          <Grid item>
            <Typography variant="body1">
              Please select the Model to use for the Multiplex Tissue Segmentation Mask task.
            </Typography>
          </Grid>
          <Grid item>
            <SelectModelStep<IFormValues, 'modelPath'>
              modelUrlField="modelPath"
              casesParams={multiplexCasesParams}
              formMethods={formMethods}
              modelType={modelTypeTissueSegmentationMask.apiModelValue}
              slideStain={MULTIPLEX_STAIN_ID}
            />
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(0),
    },
    {
      label: 'Job Name and Description',
      subLabel: activeStep !== 1 && watch('jobName'),
      content: (
        <JobNameAndDescriptionStep
          control={control}
          errors={errors}
          register={register}
          onChangeName={() => checkValidationAndSetIsStepFailed(1)}
        />
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(1),
    },
  ];

  if (isLoadingSelection) {
    return (
      <MultiplexSelectionAlert
        severity="info"
        title="Checking slide selection"
        message="Resolving the multiplex slides for the current case selection."
      />
    );
  }

  if (multiplexSelectionBlockingMessage) {
    return (
      <MultiplexSelectionAlert
        severity="error"
        title="Select slides for this multiplex action"
        message={multiplexSelectionBlockingMessage}
      />
    );
  }

  return (
    <Grid container direction="column" spacing={2}>
      {multiplexSelectionInfoMessage ? (
        <Grid item>
          <MultiplexSelectionAlert
            severity="info"
            title="This action runs on slides"
            message={multiplexSelectionInfoMessage}
          />
        </Grid>
      ) : null}
      <Grid item>
        <ExecutionRequirementsPanel requirements={executionRequirements.requirements} />
      </Grid>
      <Grid item>
        <ActionStepper
          activeStateHandlers={activeStateHandlers}
          hideExecution={!executionRequirements.canExecute}
          handleSubmit={handleSubmit(onSubmit)}
          steps={steps}
          setActiveStepForValidation={setActiveStep}
          isStepFailed={isStepFailed}
          validateStep={() => checkValidationAndSetIsStepFailed(activeStep)}
        />
      </Grid>
    </Grid>
  );
};
